/**
 * DeletedFilesPanel Component
 * 
 * Shows soft-deleted files that can be recovered within 30 days.
 * Allows admins to restore accidentally deleted files.
 */

import { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Trash2, RotateCcw, Loader2, Clock, User, FileText, AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { formatDistanceToNow } from 'date-fns';
import { sv } from 'date-fns/locale';

interface DeletedFile {
  id: string;
  original_filename: string;
  storage_path: string;
  size_bytes: number;
  deleted_at: string;
  deleted_by: string | null;
  module_type: string | null;
  file_key: string | null;
  deleted_by_profile?: {
    full_name: string | null;
    email: string | null;
  } | null;
}

interface DeletedFilesPanelProps {
  auditId: string;
  onFileRestored?: () => void;
}

// Helper to format file size
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

// Calculate days remaining until permanent deletion
function getDaysRemaining(deletedAt: string): number {
  const deletedDate = new Date(deletedAt);
  const expiryDate = new Date(deletedDate);
  expiryDate.setDate(expiryDate.getDate() + 30);
  
  const now = new Date();
  const diffMs = expiryDate.getTime() - now.getTime();
  return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
}

export function DeletedFilesPanel({ auditId, onFileRestored }: DeletedFilesPanelProps) {
  const { toast } = useToast();
  const [deletedFiles, setDeletedFiles] = useState<DeletedFile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [permanentlyDeletingId, setPermanentlyDeletingId] = useState<string | null>(null);

  // Fetch deleted files for this audit
  const fetchDeletedFiles = useCallback(async () => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase
        .from('audit_documents')
        .select(`
          id,
          original_filename,
          storage_path,
          size_bytes,
          deleted_at,
          deleted_by,
          module_type,
          file_key
        `)
        .eq('audit_id', auditId)
        .not('deleted_at', 'is', null)
        .order('deleted_at', { ascending: false });

      if (error) throw error;

      // Fetch profile info for deleted_by users
      const filesWithProfiles: DeletedFile[] = [];
      for (const file of data || []) {
        let profile = null;
        if (file.deleted_by) {
          const { data: profileData } = await supabase
            .from('profiles')
            .select('full_name, email')
            .eq('id', file.deleted_by)
            .single();
          profile = profileData;
        }
        filesWithProfiles.push({
          ...file,
          deleted_by_profile: profile,
        });
      }

      setDeletedFiles(filesWithProfiles);
    } catch (error) {
      console.error('Error fetching deleted files:', error);
      toast({
        title: 'Kunde inte hämta raderade filer',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [auditId, toast]);

  useEffect(() => {
    fetchDeletedFiles();
  }, [fetchDeletedFiles]);

  // Restore a soft-deleted file
  const handleRestore = useCallback(async (fileId: string) => {
    try {
      setRestoringId(fileId);

      const { error } = await supabase
        .from('audit_documents')
        .update({ 
          deleted_at: null, 
          deleted_by: null 
        })
        .eq('id', fileId);

      if (error) throw error;

      toast({
        title: 'Fil återställd',
        description: 'Filen är nu synlig igen',
      });

      await fetchDeletedFiles();
      onFileRestored?.();
    } catch (error) {
      console.error('Error restoring file:', error);
      toast({
        title: 'Kunde inte återställa filen',
        variant: 'destructive',
      });
    } finally {
      setRestoringId(null);
    }
  }, [toast, fetchDeletedFiles, onFileRestored]);

  // Permanently delete a file (only for admins)
  const handlePermanentDelete = useCallback(async (fileId: string, storagePath: string) => {
    try {
      setPermanentlyDeletingId(fileId);

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('audit-documents')
        .remove([storagePath]);

      if (storageError) {
        console.error('Storage delete error:', storageError);
        // Continue with DB deletion
      }

      // Delete from database permanently
      const { error: dbError } = await supabase
        .from('audit_documents')
        .delete()
        .eq('id', fileId);

      if (dbError) throw dbError;

      toast({
        title: 'Fil permanent raderad',
        description: 'Filen kan inte längre återställas',
      });

      await fetchDeletedFiles();
    } catch (error) {
      console.error('Error permanently deleting file:', error);
      toast({
        title: 'Kunde inte radera filen permanent',
        variant: 'destructive',
      });
    } finally {
      setPermanentlyDeletingId(null);
    }
  }, [toast, fetchDeletedFiles]);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Laddar raderade filer...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (deletedFiles.length === 0) {
    return null; // Don't show the panel if there are no deleted files
  }

  return (
    <Card className="border-amber-200 bg-amber-50/50 dark:border-amber-900 dark:bg-amber-950/20">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Trash2 className="h-5 w-5 text-amber-600" />
          <CardTitle className="text-lg">Raderade filer ({deletedFiles.length})</CardTitle>
        </div>
        <CardDescription>
          Filer som raderats kan återställas inom 30 dagar. Efter det tas de bort permanent.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {deletedFiles.map((file) => {
            const daysRemaining = getDaysRemaining(file.deleted_at);
            const isExpiringSoon = daysRemaining <= 7;
            
            return (
              <div 
                key={file.id} 
                className="flex items-center justify-between p-3 bg-background rounded-lg border"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <FileText className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{file.original_filename}</p>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                      <span>{formatFileSize(file.size_bytes)}</span>
                      {file.deleted_by_profile && (
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {file.deleted_by_profile.full_name || file.deleted_by_profile.email || 'Okänd'}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDistanceToNow(new Date(file.deleted_at), { addSuffix: true, locale: sv })}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Badge 
                    variant={isExpiringSoon ? "destructive" : "secondary"}
                    className="text-xs whitespace-nowrap"
                  >
                    {isExpiringSoon && <AlertTriangle className="h-3 w-3 mr-1" />}
                    {daysRemaining} dagar kvar
                  </Badge>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleRestore(file.id)}
                    disabled={restoringId === file.id}
                    className="gap-1"
                  >
                    {restoringId === file.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RotateCcw className="h-4 w-4" />
                    )}
                    Återställ
                  </Button>
                  
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        disabled={permanentlyDeletingId === file.id}
                      >
                        {permanentlyDeletingId === file.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Permanent radering</AlertDialogTitle>
                        <AlertDialogDescription>
                          Är du säker på att du vill ta bort "{file.original_filename}" permanent? 
                          Denna åtgärd kan inte ångras.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Avbryt</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={() => handlePermanentDelete(file.id, file.storage_path)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Radera permanent
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
